import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Grid,
  Select,
  MenuItem,
  FormControl,
  Button,
  Autocomplete,
  Divider,
  createFilterOptions
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import EditableItemList from './common/EditableItemList';
import { toPickerValue, fromPickerValue } from '../../../../utils/dateOnly';
import { useCV } from '../contexts/CVContext';
import { formatDate } from '../helpers/ProcessTemplate';
import { cvManagementApi } from '../../../../services/api/cvManagementApi';
import AddIcon from '@mui/icons-material/Add';

const catalogFilter = createFilterOptions();

const Certifications = () => {
  const { isDataReady, cvData, updateCertifications, readonly } = useCV();

  const [issuers, setIssuers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // Initialize with a default certification if none exist
  const [certifications, setCertifications] = useState([]);

  const [expandedIndex, setExpandedIndex] = useState(null);

  // Fetch certification issuers
  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await cvManagementApi.getGeneralCertificationIssuers();
        setIssuers(response || []);
      } catch (error) {
        console.error('Error fetching certification issuers:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, []);

  // Update local state when context data changes, but only on initial load
  useEffect(() => {
    if (cvData.certifications?.length > 0 && isDataReady) {
      setCertifications(cvData.certifications);
    }
  }, [cvData.certifications, isDataReady]);

  // Custom handler to update both local state and context
  const handleCertificationsChange = (newCertifications) => {
    // Update the order property for each certification
    const orderedCertifications = newCertifications.map((cert, index) => ({
      ...cert,
      order: index + 1
    }));

    setCertifications(orderedCertifications);
    updateCertifications(orderedCertifications);
  };

  // Function to create a new empty certification
  const createNewCertification = () => ({
    id: null,
    name: '',
    issuer: null,
    certificationCatalog: null,
    certificationCategory: null,
    issuedDate: null,
    expirationDate: null,
    website: '',
    order: certifications.length + 1
  });

  // Get catalog entries for a given issuer
  const getCatalogForIssuer = (issuerId) => {
    const issuer = issuers.find(i => i.id === issuerId);
    return issuer?.catalogCertifications || [];
  };

  // Function to render the summary of a certification (collapsed view)
  const renderCertificationSummary = (certification) => (
    <>
      <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
        {certification.name || '(Not specified)'}
      </Typography>
      {certification.issuer?.name && (
        <Typography variant="body2" color="text.secondary">
          {certification.issuer.name}
        </Typography>
      )}
      {certification.certificationCategory?.name && (
        <Typography variant="body2" color="text.secondary">
          Category: {certification.certificationCategory.name}
        </Typography>
      )}
      {certification.issuedDate && (
        <Typography variant="body2" color="text.secondary">
          Issued: {formatDate(certification.issuedDate)}
        </Typography>
      )}
      {certification.expirationDate && (
        <Typography variant="body2" color="text.secondary">
          Expires: {formatDate(certification.expirationDate)}
        </Typography>
      )}
    </>
  );

  // Function to render the edit form for a certification (expanded view)
  const renderCertificationEditForm = (certification, onChange) => {
    const isCustomIssuer = certification.issuer?.isCustom;
    const selectedIssuerId = certification.issuer?.id;
    const catalog = selectedIssuerId && !isCustomIssuer ? getCatalogForIssuer(selectedIssuerId) : [];
    const hasCatalog = catalog.length > 0;
    const catalogExpires = certification.certificationCatalog?.expires;
    // Custom cert name: explicitly flagged as custom, OR has a name but no catalog match (e.g. after reload)
    const isCustomCertName = certification.certificationCatalog?.isCustom ||
      (hasCatalog && !isCustomIssuer && certification.name && !certification.certificationCatalog?.id);

    return (
      <Grid
        container
        spacing={2}
        sx={{
          width: '100%',
          margin: '0 auto',
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            md: 'repeat(2, 1fr)'
          },
          gap: '16px',
          '& .MuiGrid-item': {
            padding: '0',
            width: '100%'
          }
        }}
      >
        {/* Issuer field */}
        <Grid item>
          <Box sx={{ mb: 2 }}>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
              Issuer <span style={{ color: 'red' }}>*</span>
            </Typography>
            {isCustomIssuer ? (
              <Box sx={{ display: 'flex', gap: 1 }}>
                <TextField
                  fullWidth
                  variant="outlined"
                  value={certification.issuer?.name || ''}
                  onChange={(e) => onChange({
                    ...certification,
                    issuer: {
                      ...certification.issuer,
                      name: e.target.value
                    }
                  })}
                  placeholder="Enter custom issuer"
                  size="small"
                  autoComplete="new-password"
                  required
                  disabled={readonly}
                />
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => onChange({
                    ...certification,
                    issuer: {
                      id: '',
                      name: '',
                      isCustom: false
                    },
                    name: '',
                    certificationCatalog: null
                  })}
                  sx={{ minWidth: 'auto', whiteSpace: 'nowrap' }}
                  disabled={readonly}
                >
                  Use list
                </Button>
              </Box>
            ) : (
              <Box>
                <FormControl fullWidth size="small" required>
                  <Select
                    value={certification.issuer?.id || ''}
                    onChange={(e) => {
                      if (e.target.value === 'custom') {
                        onChange({
                          ...certification,
                          issuer: {
                            id: 'custom',
                            name: '',
                            isCustom: true
                          },
                          name: '',
                          certificationCatalog: null
                        });
                      } else {
                        const selectedIssuer = issuers.find(issuer => issuer.id === e.target.value);
                        onChange({
                          ...certification,
                          issuer: {
                            id: selectedIssuer.id,
                            name: selectedIssuer.name,
                            isCustom: false,
                            expires: selectedIssuer.expires
                          },
                          // Reset cert name and catalog when issuer changes
                          name: '',
                          certificationCatalog: null,
                          certificationCategory: null,
                          expirationDate: null
                        });
                      }
                    }}
                    displayEmpty
                    disabled={readonly}
                    sx={{ backgroundColor: 'white' }}
                  >
                    <MenuItem value="" disabled>
                      <em>Select issuer</em>
                    </MenuItem>
                    {issuers.map((issuer) => (
                      <MenuItem key={issuer.id} value={issuer.id}>
                        {issuer.name}
                      </MenuItem>
                    ))}
                    <MenuItem value="custom" sx={{ color: 'primary.main', fontWeight: 500 }}>
                      <AddIcon fontSize="small" sx={{ mr: 1 }} />
                      Add custom issuer
                    </MenuItem>
                  </Select>
                </FormControl>
              </Box>
            )}
          </Box>
        </Grid>

        {/* Certification Name field - only visible after issuer is selected */}
        {(isCustomIssuer || selectedIssuerId) && <Grid item>
          <Box sx={{ mb: 2 }}>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
              Certification Name <span style={{ color: 'red' }}>*</span>
            </Typography>
            {isCustomCertName ? (
              <Box sx={{ display: 'flex', gap: 1 }}>
                <TextField
                  fullWidth
                  variant="outlined"
                  value={certification.name || ''}
                  onChange={(e) => onChange({
                    ...certification,
                    name: e.target.value
                  })}
                  placeholder="Enter custom certification name"
                  size="small"
                  autoComplete="new-password"
                  required
                  disabled={readonly}
                />
                {hasCatalog && !isCustomIssuer && (
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => onChange({
                      ...certification,
                      name: '',
                      certificationCatalog: null,
                      certificationCategory: null,
                      expirationDate: null
                    })}
                    sx={{ minWidth: 'auto', whiteSpace: 'nowrap' }}
                    disabled={readonly}
                  >
                    Use list
                  </Button>
                )}
              </Box>
            ) : hasCatalog && !isCustomIssuer ? (
              <Autocomplete
                options={catalog}
                getOptionLabel={(option) => option.name || ''}
                value={certification.certificationCatalog?.id
                  ? catalog.find(c => c.id === certification.certificationCatalog.id) || null
                  : null}
                onChange={(e, newValue) => {
                  if (newValue && newValue.isAddCustom) {
                    onChange({
                      ...certification,
                      name: '',
                      certificationCatalog: { isCustom: true },
                      certificationCategory: null,
                      expirationDate: null
                    });
                  } else if (newValue) {
                    onChange({
                      ...certification,
                      name: newValue.name,
                      certificationCatalog: {
                        id: newValue.id,
                        name: newValue.name,
                        expires: newValue.expires || false
                      },
                      certificationCategory: newValue.category || null,
                      ...(!newValue.expires && { expirationDate: null })
                    });
                  } else {
                    onChange({
                      ...certification,
                      name: '',
                      certificationCatalog: null,
                      certificationCategory: null,
                      expirationDate: null
                    });
                  }
                }}
                filterOptions={(options, params) => {
                  const filtered = catalogFilter(options, params);
                  filtered.push({
                    isAddCustom: true,
                    name: 'Add custom certification'
                  });
                  return filtered;
                }}
                renderOption={(props, option, { index }) => (
                  <React.Fragment key={option.id || 'add-custom'}>
                    {option.isAddCustom && <Divider />}
                    <li {...props}>
                      {option.isAddCustom ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', color: 'primary.main', fontWeight: 500 }}>
                          <AddIcon fontSize="small" sx={{ mr: 1 }} />
                          Add custom certification
                        </Box>
                      ) : (
                        <Box sx={{ width: '100%' }}>
                          <Typography variant="body2">{option.name}</Typography>
                          {option.category && (
                            <Typography variant="caption" color="text.secondary">
                              {option.category.name}
                            </Typography>
                          )}
                        </Box>
                      )}
                    </li>
                    {!option.isAddCustom && index < catalog.length - 1 && <Divider />}
                  </React.Fragment>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Search or select certification"
                    size="small"
                    required
                    disabled={readonly}
                  />
                )}
                isOptionEqualToValue={(option, value) => option.id === value?.id}
                disabled={readonly}
                sx={{ backgroundColor: 'white' }}
              />
            ) : (
              <TextField
                fullWidth
                variant="outlined"
                value={certification.name || ''}
                onChange={(e) => onChange({ ...certification, name: e.target.value })}
                placeholder="Enter certification name"
                size="small"
                autoComplete="new-password"
                required
                disabled={readonly}
              />
            )}
          </Box>
        </Grid>}

        {/* Issued Date */}
        <Grid item>
          <Box sx={{ mb: 2 }}>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
              Issued Date <span style={{ color: 'red' }}>*</span>
            </Typography>
            <DatePicker
              views={['year', 'month']}
              value={toPickerValue(certification.issuedDate)}
              onChange={(date) => onChange({ ...certification, issuedDate: fromPickerValue(date) })}
              disabled={readonly}
              slotProps={{
                textField: {
                  fullWidth: true,
                  size: "small",
                  placeholder: "MM / YYYY",
                  required: true
                }
              }}
            />
          </Box>
        </Grid>

        {/* Expiration Date - always visible, mandatory only if catalog certification has expires=true */}
        <Grid item>
          <Box sx={{ mb: 2 }}>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
              Expiration Date {catalogExpires && <span style={{ color: 'red' }}>*</span>}
            </Typography>
            <DatePicker
              views={['year', 'month']}
              value={toPickerValue(certification.expirationDate)}
              onChange={(date) => onChange({ ...certification, expirationDate: fromPickerValue(date) })}
              disabled={readonly}
              slotProps={{
                textField: {
                  fullWidth: true,
                  size: "small",
                  placeholder: "MM / YYYY",
                  required: catalogExpires
                }
              }}
            />
          </Box>
        </Grid>

        {/* Website */}
        <Grid item>
          <Box sx={{ mb: 2 }}>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
              Website
            </Typography>
            <TextField
              fullWidth
              variant="outlined"
              value={certification.website || ''}
              onChange={(e) => onChange({ ...certification, website: e.target.value })}
              placeholder="https://example.com/certificate"
              size="small"
              autoComplete="new-password"
              name={`cv_cert_website_${Math.random().toString(36).substring(2, 9)}`}
              disabled={readonly}
            />
          </Box>
        </Grid>

      </Grid>
    );
  };

  return (
    <EditableItemList
      items={certifications}
      setItems={handleCertificationsChange}
      expandedIndex={expandedIndex}
      setExpandedIndex={setExpandedIndex}
      createNewItem={createNewCertification}
      renderSummary={renderCertificationSummary}
      renderEditForm={renderCertificationEditForm}
      addButtonText={certifications.length > 0 ? "Add one more certification" : "Add a certification"}
      emptyState={{
        title: "You haven't added any certifications yet.",
        description: "Add any relevant certifications you've earned."
      }}
      itemName="certification"
      isLoading={isLoading}
      disabled={isLoading}
      readonly={readonly}
    />
  );
};

export default Certifications;
